import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type {
  DeleteBlocker,
  ImportApplyResult,
  ImportPreview,
  PromoteEvidence,
  VersionImportEnvelope,
  WorkflowVersionRow,
} from '@/types/workflowVersions'

export function workflowVersionsQueryKey(workflowId: string, includeArchived: boolean) {
  return ['workflows', 'versions', workflowId, includeArchived] as const
}

export function useWorkflowVersions(
  workflowId: string | undefined,
  includeArchived: boolean,
): UseQueryResult<WorkflowVersionRow[], Error> {
  return useQuery({
    queryKey: workflowVersionsQueryKey(workflowId ?? '', includeArchived),
    queryFn: () =>
      apiClient.get<WorkflowVersionRow[]>(
        `/api/workflows/${workflowId}/versions?include_archived=${includeArchived}`,
      ),
    enabled: !!workflowId,
    staleTime: 0,
  })
}

function useInvalidateWorkflows() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['workflows'] })
}

export function useSetWorkflowVersionTag(
  workflowId: string,
): UseMutationResult<unknown, Error, { versionNumber: number; tag: string }> {
  const invalidate = useInvalidateWorkflows()
  return useMutation({
    mutationFn: ({ versionNumber, tag }) =>
      apiClient.put(`/api/workflows/${workflowId}/versions/${versionNumber}/tag`, { tag }),
    onSuccess: invalidate,
  })
}

async function expectOk(res: Response): Promise<void> {
  if (!res.ok) throw new Error(`Request failed (${res.status})`)
}

export function useRemoveWorkflowTag(
  workflowId: string,
): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateWorkflows()
  return useMutation({
    mutationFn: async (tag: string) => {
      const res = await apiClient.fetch(
        `/api/workflows/${workflowId}/tags/${encodeURIComponent(tag)}`,
        { method: 'DELETE' },
      )
      await expectOk(res)
    },
    onSuccess: invalidate,
  })
}

export function useArchiveWorkflowVersion(
  workflowId: string,
): UseMutationResult<void, Error, { versionNumber: number; archive: boolean }> {
  const invalidate = useInvalidateWorkflows()
  return useMutation({
    mutationFn: async ({ versionNumber, archive }) => {
      const res = await apiClient.fetch(
        `/api/workflows/${workflowId}/versions/${versionNumber}/${archive ? 'archive' : 'unarchive'}`,
        { method: 'POST' },
      )
      await expectOk(res)
    },
    onSuccess: invalidate,
  })
}

export class VersionDeleteBlockedError extends Error {
  blockers: DeleteBlocker[]
  constructor(blockers: DeleteBlocker[]) {
    super('Version cannot be deleted')
    this.blockers = blockers
  }
}

export function useDeleteWorkflowVersion(
  workflowId: string,
): UseMutationResult<void, Error, number> {
  const invalidate = useInvalidateWorkflows()
  return useMutation({
    mutationFn: async (versionNumber: number) => {
      const res = await apiClient.fetch(
        `/api/workflows/${workflowId}/versions/${versionNumber}`,
        { method: 'DELETE' },
      )
      if (res.status === 409) {
        const body = await res.json()
        throw new VersionDeleteBlockedError(body?.detail?.blockers ?? [])
      }
      await expectOk(res)
    },
    onSuccess: invalidate,
  })
}

export function useRestoreWorkflowVersion(
  workflowId: string,
): UseMutationResult<unknown, Error, number> {
  const invalidate = useInvalidateWorkflows()
  return useMutation({
    mutationFn: (versionNumber: number) =>
      apiClient.post(`/api/workflows/${workflowId}/versions/${versionNumber}/restore`, {}),
    onSuccess: invalidate,
  })
}

export function usePromoteWorkflowVersion(
  workflowId: string,
): UseMutationResult<WorkflowVersionRow, Error, PromoteEvidence> {
  const invalidate = useInvalidateWorkflows()
  return useMutation({
    // The promote endpoint responds with the version-DETAIL projection, not
    // the enriched list row — `tags` / `delete_blockers` / `created_by_name`
    // / `archived_at` come back here as plain schema defaults, not real
    // enrichment. Callers relying on those fields should wait for the
    // invalidated GET /versions refetch instead of reading this response.
    mutationFn: (evidence) =>
      apiClient.post<WorkflowVersionRow>(`/api/workflows/${workflowId}/promote`, { evidence }),
    onSuccess: invalidate,
  })
}

export function useWorkflowVersionImportPreview(
  workflowId: string,
): UseMutationResult<ImportPreview, Error, VersionImportEnvelope> {
  return useMutation({
    mutationFn: (envelope) =>
      apiClient.post<ImportPreview>(
        `/api/workflows/${workflowId}/versions/import/preview`,
        envelope,
      ),
  })
}

export function useWorkflowVersionImportApply(
  workflowId: string,
): UseMutationResult<ImportApplyResult, Error, VersionImportEnvelope> {
  const invalidate = useInvalidateWorkflows()
  return useMutation({
    mutationFn: (envelope) =>
      apiClient.post<ImportApplyResult>(
        `/api/workflows/${workflowId}/versions/import/apply`,
        envelope,
      ),
    onSuccess: invalidate,
  })
}

/** Auth-gated blob download — the JWT lives in memory, so a plain <a href>
 * would 401 (same idiom as DocsIndexPage.downloadUserGuide). */
export async function downloadWorkflowVersionExport(
  workflowId: string,
  versionNumber: number,
): Promise<void> {
  const res = await apiClient.fetch(
    `/api/workflows/${workflowId}/versions/${versionNumber}/export`,
  )
  if (!res.ok) throw new Error(`Export failed (${res.status})`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${workflowId}.v${versionNumber}.workflow-version.json`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
